import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { reportUiError } from '../../sentry';

export type HeldEmailListRow = {
  id: string;
  kind: string;
  status: string;
  subject: string;
  proposed_send_at: string | null;
  article_title: string | null;
  article_url: string | null;
  subscriber_id: string | null;
  source_transcript_id: string | null;
  signals: Record<string, unknown> | null;
  created_at: string;
  subscribers?: { name: string | null; email: string | null } | null;
};

const STATUS_FILTERS = ['held', 'approved', 'dismissed', 'all'] as const;

function confidenceOf(signals: Record<string, unknown> | null): string {
  const c = signals?.confidence;
  return typeof c === 'string' ? c : '—';
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const HeldEmailsView: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<HeldEmailListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('held');
  const [searchQuery, setSearchQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let q = supabase
        .from('held_emails')
        .select(
          'id, kind, status, subject, proposed_send_at, article_title, article_url, subscriber_id, source_transcript_id, signals, created_at, subscribers(name, email)',
        )
        .order('proposed_send_at', { ascending: true, nullsFirst: false })
        .limit(100);

      if (statusFilter !== 'all') {
        q = q.eq('status', statusFilter);
      }

      const { data, error } = await q;
      if (error) throw error;
      setRows((data as HeldEmailListRow[]) ?? []);
    } catch (e) {
      reportUiError('held_emails', 'list', e);
      toast.error(e instanceof Error ? e.message : 'Could not load held emails');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = rows.filter((row) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const hay = [
      row.subject,
      row.article_title,
      row.kind,
      row.subscribers?.name,
      row.subscribers?.email,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Mail className="w-6 h-6 text-amber-500" />
            Held emails
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Proposed sends waiting for your approve or dismiss. Nothing leaves until you promote.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search subject, person, article…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 text-xs font-medium rounded-md capitalize ${
                statusFilter === s
                  ? 'bg-amber-600 text-white'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400 text-sm">
          No held emails in this filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/80 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Who</th>
                <th className="px-4 py-3">Kind</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Proposed</th>
                <th className="px-4 py-3">Article</th>
                <th className="px-4 py-3">Conf.</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  onClick={() => navigate(`/held-emails/${row.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {row.subscribers?.name || '—'}
                    </div>
                    <div className="text-xs text-gray-500">{row.subscribers?.email || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.kind}</td>
                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100 max-w-xs truncate">
                    {row.subject || '—'}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600 dark:text-gray-300">
                    {formatWhen(row.proposed_send_at)}
                  </td>
                  <td className="px-4 py-3 max-w-[10rem] truncate text-gray-700 dark:text-gray-300">
                    {row.article_title || '—'}
                  </td>
                  <td className="px-4 py-3 capitalize">{confidenceOf(row.signals)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 capitalize">
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HeldEmailsView;
